import React, {FC} from "react";
import VidaSection from "../../../../components/@vida/vida-section";
import Divider from "../divider";
import Spacer from "../spacer";
import Note from "../note";

const UsingVida: FC = () => {
  return <div>
    <VidaSection title="" id="using-vida">
      <Divider/>
      <h1>Using VIDA</h1>

      {/* VIN Profiling */}
      <Spacer/>
      <VidaSection title="VIN Profiling" id="vin-profiling">
        <p><strong>Selecting a vehicle in VIDA</strong></p>
        <p>This procedure describes how to select a vehicle.</p>
        <Note body={<>
          <p>To perform fault tracing and software download, the vehicle needs to be connected. Select a
            vehicle if a vehicle is already connected and occupied by another user or if you only need to see
            information or work with pre- planning for the vehicle.</p>
          <p>If a previous readout is available for the
            vehicle it is possible to access the Network, Fault Tracing and
            Vehicle Communication pages in an offline diagnostic mode and see the previously readout
            information.</p>
        </>}/>
        <ol>
          <li>Open any sub tab under the Home tab.</li>
          <li>Click the Select button for the vehicle you want to work with.</li>
        </ol>
        <p>The vehicle is selected and assigned its own tab.</p>
        <p>For further information and instructions on how to physically connect a vehicle to VIDA, see Vehicle
          communication how-to{"'"}s.</p>
      </VidaSection>

      {/* Repair Topics and Description */}
      <Spacer/>
      <VidaSection title="Repair Topics and Description" id="repair-topics-and-description">
        <p><strong>Repair</strong></p>
        <p>This category contains reference information about the following:</p>
        <table>
          <tr>
            <th align="left">Topics</th>
            <th align="left">Description</th>
          </tr>
          <tr>
            <td>General Safety Information</td>
            <td>Safety and warning information, e.g. safety instructions for rescue work for the chosen vehicle.</td>
          </tr>
          <tr>
            <td>General information</td>
            <td>Explanations regarding parts for the chosen vehicle model, e.g. water quality in the cooling system. In
              this category, colors and symbols used in the reference documents are also explained. The same information
              can also be reached by clicking the icons in the top right corner of the right field.
            </td>
          </tr>
          <tr>
            <td>Component location</td>
            <td>Overview images showing location of various components as well as which parts they consist of.</td>
          </tr>
          <tr>
            <td>Service programs</td>
            <td>Information displaying maintenance and controls, e.g. check lists and other service information.
              However, the most effective way to find a service program for a specific vehicle is to enter VIN at the
              Home page and go through Planning -{">"} Service programs.
            </td>
          </tr>
          <tr>
            <td>Removal, replacement and installation</td>
            <td>Instructions on how to handle components in engine, electrical systems, brakes, wheels etc. The
              information includes needed special tools, their tool numbers and descriptions.
            </td>
          </tr>
          <tr>
            <td>Overhaul instructions</td>
            <td>Components which are possible to overhaul, with locations displayed on images. The information includes
              needed special tools, their tool numbers and descriptions.
            </td>
          </tr>
          <tr>
            <td>Cleaning, inspection and adjustment</td>
            <td>Instructions on how to handle this with parts and components</td>
          </tr>
          <tr>
            <td>Installation instructions, accessories</td>
            <td>How to install accessories such as end pipes, parking assistance, infotainment and body and interior
              accessories.
            </td>
          </tr>
        </table>
        <p>In VIDA, click on the arrows next to the function groups in order to expand or collapse the headings.</p>
      </VidaSection>

      {/* Getting to repair information */}
      <Spacer/>
      <VidaSection title="Getting to repair information" id="getting-to-repair-information">
        <br/>
        <img
          src="https://cdn.volvotechinfo.com/vida-basic-training/Using%20VIDA/Getting%20to%20repair%20information_image.jpg"
          alt="Getting to repair information_image"/>
      </VidaSection>

      {/* Diagnostic Protocols */}
      <Spacer/>
      <VidaSection title="Diagnostic Protocols" id="diagnostic-protocols">
        <p><strong>Diagnostic protocols</strong></p>
        <p>Since the end of the 1980s, control module diagnosis has developed from detecting simple problems and storing
          a small amount of information to include more complicated tests and checks. Today a large amount of
          information can be read out both about the detected problem as well as parameters from the control
          modules{"'"} input and output signals. It is also possible to activate, control and write to control
          modules.</p>
        <p>In VIDA, the vehicle communication methods differ depending on what diagnostic protocol a vehicle model is
          using. Also, the diagnostic protocols provide different possibilities in terms of how the vehicle model can be
          diagnosed in VIDA. Because of this, the approach to diagnosis under the Diagnostics menu on thePlanning &
          Diagnostics tab is slightly different, depending on what protocol the diagnosed vehicle is using.</p>
        <p>The vehicle models handled in VIDA are divided into the following two main groups, based on diagnostic
          protocols:</p>
        <ul>
          <li>VDS protocol-based vehicle models (used in all vehicles on the new electrical platform, starting with
            XC90 model year 2016).
          </li>
          <li>D2 and GGD protocol-based vehicle models (used by all vehicles on all other existing platforms).</li>
        </ul>
        <Note body={<p>Throughout this help file, certain functionalities and procedures are described twice, based on
          these two groups.</p>}/>
        <p>For more information regarding VDS diagnostic protocol, read the document Diagnostic communication in VIDA.
          Find this document under a vehicle{"'"}s Information tab by searching or via the following path: </p>
        <p>Product specifications -{">"} Function -{">"} 0 Administration</p>
        <p>For more information regarding D2 and GGD diagnostic protocols, read the document Diagnostic versions and
          communication methods in VIDA. Find this document under a vehicle{"'"}s Information tab by search or via the
          following path: </p>
        <p>Product specifications -{">"} Design and Function -{">"} 0 Administration -{">"} 09 Miscellaneous
          -{">"} Diagnosis</p>
        <p><strong>You may also be interested in:</strong></p>
        <p>VDS protocol</p>
        <p>D2 and GGD protocol</p>
      </VidaSection>

      {/* Advanced test */}
      <Spacer/>
      <VidaSection title="Advanced test" id="advanced-test">
        <p><strong>Advanced Test</strong></p>
        <p>This functionality is accessed by clicking the Advanced Test button on the Software Installation tab. The
          advanced test functionality is used to check the preparedness of vehicles before starting a software download.
          You can e.g. check if the connectivity with PIE is working and check if control units are responding. </p>
        <img src="https://cdn.volvotechinfo.com/vida-basic-training/Using%20VIDA/Advanced%20test_image.jpg"
             alt="Advanced test_image"/>
        <p>Fig. 229: Advanced Test for D2 and GGD protocol-based vehicles (left) and for VDS protocol-based vehicles
          (right) </p>
        <ol>
          <li>Control unit connection
            <p>In the list, you can see all possible ECUs found in the central system for the connected vehicle. On the
              VDS protocol-based vehicles, you can also change between the Existing ECUs and All ECUs tabs. </p>
            <p>First, click the Enable Programming Mode button to enable testing of communication with control units in
              the current vehicle. </p>
            <p>By clicking any ECU in the list, you can see status on the vehicle{"'"}s control modules</p>
            <Note body={<p>If all control modules are disabled, no readout information is available.</p>}/>
            <p><strong>Test communication with control units</strong></p>
            <p>Click the Enable Programming Mode button to enable testing of communication with control units in the
              current vehicle. When the button is clicked, the vehicle is set to program mode and you can select a
              control module in the vehicle graphic to read the hardware and serial numbers. If these two numbers are
              displayed in the two fields below the buttons, the selected control module is responsive to communication.
              Click the Reset button to disable program mode and return to default mode. </p>
          </li>
          <li>VIN Reading
            <p>Clicking the Read VIN button fetches the current vehicle{"'"}s VIN and displays it in the text field. The
              VIN can be copied from this field if needed. </p>
            <Note body={<p>It is not possible to read VIN if the vehicle is set to program mode (see section 1). </p>}/>
            <br/>
          </li>
          <li>USB handling (Only applicable for D2 and GGD protocolbased vehicles)
            <p>Tick this checkbox to enable download of large software files without using a USB memory. </p>
            <Note body={<p>This functionality is not applicable for ICM and IHU software.</p>}/>
            <br/>
          </li>
          <li>Communication Tool (Only applicable for D2 and GGD
            protocol-based vehicles)
            <p>This functionality is only available if your VIDA workstation is connected to the vehicle through a
              vehicle communication tool. By clicking the Test Communication Tool button, a test is performed and you
              are notified whether the communication between the communication tool and VIDA is working or not. </p>
          </li>
          <li>PIE Connection
            <p>By clicking the Test PIE Connection button, a test is performed to test the connection with PIE. You are
              notified whether the connection is working or not. If the connection is not working properly, an error
              message is shown. </p>
          </li>
        </ol>
        <p><strong>You may also be interested in:</strong></p>
        <p>Vehicle communication with DiCE how-tos (D2 and GGD)</p>
        <p>Vehicle communication tools</p>
        <p>PIE</p>
      </VidaSection>
    </VidaSection>
    <style jsx>{`
      img {
        width: 100%;
      }

      table {
        border-collapse: collapse;
      }

      tr:nth-child(even) {
        background-color: #eee;
      }

      th, td {
        padding: 8px;
      }
    `}</style>
  </div>
}

export default UsingVida;
